import React, { useState, useRef, } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const Contact = () => {
  const [focusedField, setFocusedField] = useState(null);
  const [hoveredContact, setHoveredContact] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recaptchaRef = useRef(null);

  const colors = {
    white: '#ffffff',
    bg: '#f5faff',
    bgSoft: '#eef8ff',
    blue: '#0284c7',
    blueDark: '#0369a1',
    cyan: '#38bdf8',
    navy: '#0f172a',
    text: '#475569',
    muted: '#64748b',
    border: '#dbeafe',
    borderDark: '#bfdbfe',
    green: '#0f766e',
  };

  const contactItems = [
    {
      icon: '✉',
      title: 'Email Us',
      value: 'info@sansaconsultant.com',
      description: 'Send your project requirements and we will get back to you.',
    },
    {
      icon: '☎',
      title: 'Call Our Team',
      value: '+91 9970111158 / +91 9766506624',
      description: 'Speak directly with our engineering coordination team.',
    },
    {
      icon: '⌖',
      title: 'Our Location',
      value: 'B1703 KOHINOOR GRANDEUR B WING, RAVET, PUNE - 412101, Maharashtra, India',
      description: 'Engineering operations and project coordination from Pune.',
    },
  ];

  const projectTypes = [
    'Structural Design',
    'Structural Analysis',
    'Steel Structures',
    'Tekla Modelling',
    'Land Development',
    'Civil / CAD Drafting',
    'Other',
  ];

  const styles = {
    section: {
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      padding: '8rem 5% 7rem',
      background:
        'linear-gradient(135deg, #ffffff 0%, #f7fbff 45%, #edf8ff 100%)',
      overflow: 'hidden',
      fontFamily:
        '"Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      boxSizing: 'border-box',
    },

    gridBackground: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'linear-gradient(rgba(2,132,199,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(2,132,199,0.035) 1px, transparent 1px)',
      backgroundSize: '45px 45px',
      animation: 'contactGridMove 25s linear infinite',
      pointerEvents: 'none',
    },

    glowOne: {
      position: 'absolute',
      width: '550px',
      height: '550px',
      borderRadius: '50%',
      background: 'rgba(56,189,248,0.10)',
      filter: 'blur(110px)',
      top: '-280px',
      left: '-180px',
      animation: 'contactGlowOne 10s ease-in-out infinite',
      pointerEvents: 'none',
    },

    glowTwo: {
      position: 'absolute',
      width: '500px',
      height: '500px',
      borderRadius: '50%',
      background: 'rgba(2,132,199,0.08)',
      filter: 'blur(110px)',
      bottom: '-250px',
      right: '-180px',
      animation: 'contactGlowTwo 12s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1350px',
      margin: '0 auto',
    },

    header: {
      maxWidth: '900px',
      margin: '0 auto',
      textAlign: 'center',
    },

    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      padding: '9px 18px',
      borderRadius: '999px',
      background: colors.bgSoft,
      border: `1px solid ${colors.border}`,
      color: colors.blueDark,
      fontSize: '0.72rem',
      fontWeight: '800',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: '1.7rem',
    },

    badgeDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.blue,
      animation: 'contactPulse 2s infinite',
    },

    heading: {
      margin: 0,
      color: colors.navy,
      fontSize: 'clamp(3rem, 6vw, 5.8rem)',
      lineHeight: '0.95',
      letterSpacing: '-0.06em',
      fontWeight: '850',
    },

    headingAccent: {
      color: colors.blue,
      position: 'relative',
      display: 'inline-block',
    },

    headingUnderline: {
      position: 'absolute',
      left: '2%',
      right: '2%',
      bottom: '-10px',
      height: '9px',
      background: '#dff2ff',
      borderRadius: '20px',
      zIndex: -1,
    },

    intro: {
      maxWidth: '720px',
      margin: '2rem auto 0',
      color: colors.text,
      fontSize: '1.08rem',
      lineHeight: '1.8',
    },

    contactLayout: {
      marginTop: '5.5rem',
      display: 'grid',
      gridTemplateColumns: '0.8fr 1.2fr',
      gap: '2rem',
      alignItems: 'stretch',
    },

    informationPanel: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '30px',
      padding: '3rem',
      background:
        'linear-gradient(145deg, #0f172a 0%, #123b56 55%, #075985 100%)',
      boxShadow: '0 30px 70px rgba(15,23,42,0.18)',
      minHeight: '650px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },

    technicalLines: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'linear-gradient(90deg, rgba(125,211,252,0.07) 1px, transparent 1px), linear-gradient(rgba(125,211,252,0.07) 1px, transparent 1px)',
      backgroundSize: '40px 40px',
      animation: 'technicalMove 18s linear infinite',
    },

    ringOne: {
      position: 'absolute',
      width: '400px',
      height: '400px',
      borderRadius: '50%',
      border: '1px solid rgba(125,211,252,0.20)',
      right: '-190px',
      top: '-80px',
      animation: 'contactRotate 22s linear infinite',
    },

    ringTwo: {
      position: 'absolute',
      width: '260px',
      height: '260px',
      borderRadius: '50%',
      border: '1px dashed rgba(125,211,252,0.25)',
      right: '-80px',
      top: '0',
      animation: 'contactRotateReverse 15s linear infinite',
    },

    infoContent: {
      position: 'relative',
      zIndex: 2,
    },

    eyebrow: {
      color: '#7dd3fc',
      fontSize: '0.72rem',
      fontWeight: '800',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
    },

    infoHeading: {
      color: colors.white,
      fontSize: 'clamp(2.2rem, 4vw, 3.5rem)',
      lineHeight: '1',
      letterSpacing: '-0.045em',
      fontWeight: '800',
      margin: '0.8rem 0 1.2rem',
    },

    infoText: {
      color: '#cbd5e1',
      fontSize: '0.95rem',
      lineHeight: '1.75',
      maxWidth: '470px',
      margin: 0,
    },

    contactList: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginTop: '3rem',
    },

    contactItem: (active) => ({
      display: 'flex',
      gap: '1rem',
      padding: '1rem',
      borderRadius: '16px',
      background: active
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(255,255,255,0.055)',
      border: active
        ? '1px solid rgba(125,211,252,0.35)'
        : '1px solid rgba(255,255,255,0.08)',
      transform: active ? 'translateX(7px)' : 'translateX(0)',
      transition: 'all .35s ease',
      cursor: 'pointer',
    }),

    contactIcon: {
      width: '45px',
      height: '45px',
      flexShrink: 0,
      borderRadius: '13px',
      background: 'rgba(56,189,248,0.13)',
      color: '#7dd3fc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.2rem',
    },

    contactTitle: {
      color: '#94a3b8',
      fontSize: '0.68rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: '800',
    },

    contactValue: {
      color: '#ffffff',
      fontSize: '0.9rem',
      fontWeight: '700',
      marginTop: '4px',
    },

    contactDescription: {
      color: '#94a3b8',
      fontSize: '0.72rem',
      lineHeight: '1.5',
      marginTop: '4px',
    },

    bottomStatement: {
      position: 'relative',
      zIndex: 2,
      marginTop: '2rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid rgba(255,255,255,0.10)',
      color: '#94a3b8',
      fontSize: '0.78rem',
      lineHeight: '1.6',
    },

    formPanel: {
      background: '#ffffff',
      border: `1px solid ${colors.border}`,
      borderRadius: '30px',
      padding: '3rem',
      boxShadow: '0 25px 65px rgba(15,23,42,0.07)',
    },

    formHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      marginBottom: '2rem',
    },

    formTitle: {
      margin: 0,
      color: colors.navy,
      fontSize: '2rem',
      fontWeight: '800',
      letterSpacing: '-0.04em',
    },

    formSubtitle: {
      margin: '0.5rem 0 0',
      color: colors.muted,
      fontSize: '0.85rem',
      lineHeight: '1.6',
    },

    secureBadge: {
      padding: '8px 12px',
      borderRadius: '999px',
      background: '#ecfdf5',
      color: colors.green,
      fontSize: '0.65rem',
      fontWeight: '800',
      whiteSpace: 'nowrap',
    },

    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.2rem',
    },

    fieldWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },

    fullField: {
      gridColumn: '1 / -1',
    },

    label: {
      color: colors.navy,
      fontSize: '0.73rem',
      fontWeight: '800',
      letterSpacing: '0.04em',
    },

    input: (focused) => ({
      width: '100%',
      padding: '0.95rem 1rem',
      borderRadius: '12px',
      border: `1px solid ${focused ? colors.blue : colors.border
        }`,
      background: focused ? '#fbfdff' : colors.bg,
      outline: 'none',
      color: colors.navy,
      fontSize: '0.88rem',
      transition: 'all .3s ease',
      boxShadow: focused
        ? '0 0 0 4px rgba(2,132,199,0.08)'
        : 'none',
      boxSizing: 'border-box',
    }),

    textarea: (focused) => ({
      width: '100%',
      minHeight: '135px',
      padding: '1rem',
      borderRadius: '12px',
      border: `1px solid ${focused ? colors.blue : colors.border
        }`,
      background: focused ? '#fbfdff' : colors.bg,
      outline: 'none',
      resize: 'vertical',
      color: colors.navy,
      fontSize: '0.88rem',
      lineHeight: '1.6',
      transition: 'all .3s ease',
      boxShadow: focused
        ? '0 0 0 4px rgba(2,132,199,0.08)'
        : 'none',
      boxSizing: 'border-box',
    }),

    projectGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '0.65rem',
    },

    projectOption: (selected) => ({
      padding: '0.75rem 0.55rem',
      borderRadius: '10px',
      textAlign: 'center',
      background: selected ? colors.bgBlue : colors.bg,
      border: `1px solid ${selected ? colors.blue : colors.border
        }`,
      color: selected ? colors.blueDark : colors.text,
      fontSize: '0.68rem',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all .25s ease',
    }),

    submitButton: {
      width: '100%',
      marginTop: '1.5rem',
      padding: '1.05rem 1.5rem',
      border: 'none',
      borderRadius: '13px',
      background:
        'linear-gradient(135deg, #0284c7, #0369a1)',
      color: '#ffffff',
      fontSize: '0.9rem',
      fontWeight: '800',
      cursor: 'pointer',
      boxShadow: '0 15px 30px rgba(2,132,199,0.20)',
      transition: 'all .3s ease',
    },

    responseNote: {
      textAlign: 'center',
      marginTop: '1rem',
      color: colors.muted,
      fontSize: '0.7rem',
    },

    successBox: {
      padding: '2rem',
      borderRadius: '18px',
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      color: '#065f46',
      textAlign: 'center',
      marginTop: '1rem',
    },
  };

  const [selectedProject, setSelectedProject] = useState('');

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   setSubmitted(true);
  // };


  const handleSubmit = async (e) => {
    e.preventDefault();

    setCaptchaError('');

    // Get the actual CAPTCHA token directly from reCAPTCHA
    const token = recaptchaRef.current?.getValue();

    if (!token) {
      setCaptchaError('Please verify that you are not a robot.');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);

      // Make absolutely sure the CAPTCHA response is included
      formData.set('g-recaptcha-response', token);

      const response = await fetch(
        'https://formspree.io/f/mbglkroa',
        {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.errors
            ?.map((error) => error.message)
            .join(', ') ||
          'Unable to send your enquiry.'
        );
      }

      // SUCCESS
      setSubmitted(true);

      // Reset CAPTCHA
      recaptchaRef.current?.reset();
      setCaptchaToken('');

    } catch (error) {
      console.error('Form submission error:', error);

      setCaptchaError(
        error.message ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section style={styles.section}>
      <style>{`
        @keyframes contactGridMove {
          0% { background-position: 0 0; }
          100% { background-position: 45px 45px; }
        }

        @keyframes contactGlowOne {
          0%,100% {
            transform: translate(0,0) scale(1);
          }
          50% {
            transform: translate(40px,30px) scale(1.12);
          }
        }

        @keyframes contactGlowTwo {
          0%,100% {
            transform: translate(0,0);
          }
          50% {
            transform: translate(-35px,-25px);
          }
        }

        @keyframes contactPulse {
          0%,100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(2,132,199,.35);
          }
          50% {
            transform: scale(1.35);
            box-shadow: 0 0 0 8px rgba(2,132,199,0);
          }
        }

        @keyframes technicalMove {
          0% { transform: translate(0,0); }
          100% { transform: translate(40px,40px); }
        }

        @keyframes contactRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes contactRotateReverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }

        @media (max-width: 1050px) {
          .contact-layout {
            grid-template-columns: 1fr !important;
          }

          .contact-information {
            min-height: auto !important;
          }
        }

        @media (max-width: 700px) {
          .contact-main-section {
            padding: 5rem 5% !important;
          }

          .contact-heading {
            font-size: 3rem !important;
          }

          .contact-layout {
            margin-top: 3.5rem !important;
          }

          .contact-information,
          .contact-form {
            padding: 1.6rem !important;
            border-radius: 22px !important;
          }

          .contact-form-grid {
            grid-template-columns: 1fr !important;
          }

          .contact-full-field {
            grid-column: auto !important;
          }

          .contact-project-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          .contact-form-header {
            flex-direction: column !important;
          }

          .contact-info-heading {
            font-size: 2.4rem !important;
          }
        }

        @media (max-width: 420px) {
          .contact-heading {
            font-size: 2.55rem !important;
          }

          .contact-project-grid {
            grid-template-columns: 1fr 1fr !important;
          }

          .contact-item-value {
            font-size: .78rem !important;
          }
        }
      `}</style>

      <div style={styles.gridBackground} />
      <div style={styles.glowOne} />
      <div style={styles.glowTwo} />

      <div
        style={styles.container}
        className="contact-main-section"
      >

        {/* ================= HEADER ================= */}

        <div style={styles.header}>
          <div style={styles.badge}>
            <span style={styles.badgeDot} />
            Start A Conversation
          </div>

          <h2
            style={styles.heading}
            className="contact-heading"
          >
            Let's Build
            <br />
            Something{' '}
            <span style={styles.headingAccent}>
              Strong.
              <span style={styles.headingUnderline} />
            </span>
          </h2>

          <p style={styles.intro}>
            Have a structural, modelling, land development, or
            engineering requirement? Tell us about your project and
            our team will help define the right engineering approach.
          </p>
        </div>

        {/* ================= CONTACT LAYOUT ================= */}

        <div
          style={styles.contactLayout}
          className="contact-layout"
        >

          {/* ================= LEFT PANEL ================= */}

          <div
            style={styles.informationPanel}
            className="contact-information"
          >
            <div style={styles.technicalLines} />
            <div style={styles.ringOne} />
            <div style={styles.ringTwo} />

            <div style={styles.infoContent}>
              <div style={styles.eyebrow}>
                Engineering Coordination
              </div>

              <h3
                style={styles.infoHeading}
                className="contact-info-heading"
              >
                Your project.
                <br />
                Our expertise.
              </h3>

              <p style={styles.infoText}>
                From early-stage concepts to detailed engineering
                deliverables, we work with clients, architects,
                contractors, and project teams to turn technical
                requirements into practical solutions.
              </p>

              <div style={styles.contactList}>
                {contactItems.map((item, index) => {
                  const active = hoveredContact === index;

                  return (
                    <div
                      key={index}
                      style={styles.contactItem(active)}
                      onMouseEnter={() => setHoveredContact(index)}
                      onMouseLeave={() => setHoveredContact(null)}
                    >
                      <div style={styles.contactIcon}>
                        {item.icon}
                      </div>

                      <div>
                        <div style={styles.contactTitle}>
                          {item.title}
                        </div>

                        <div
                          style={styles.contactValue}
                          className="contact-item-value"
                        >
                          {item.value}
                        </div>

                        <div style={styles.contactDescription}>
                          {item.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={styles.bottomStatement}>
              <strong style={{ color: '#ffffff' }}>
                SANSA CONSULTANT INDIA LLP
              </strong>
              <br />
              Structural Engineering • Analysis • Modelling •
              Land Development
            </div>
          </div>

          {/* ================= FORM PANEL ================= */}

          <div
            style={styles.formPanel}
            className="contact-form"
          >
            <div
              style={styles.formHeader}
              className="contact-form-header"
            >
              <div>
                <h3 style={styles.formTitle}>
                  Tell Us About Your Project
                </h3>

                <p style={styles.formSubtitle}>
                  Share a few details and our engineering team can
                  understand where to begin.
                </p>
              </div>

              <span style={styles.secureBadge}>
                ● CONFIDENTIAL
              </span>
            </div>

            {submitted ? (
              <div style={styles.successBox}>
                <div
                  style={{
                    fontSize: '2rem',
                    marginBottom: '0.7rem',
                  }}
                >
                  ✓
                </div>

                <strong>
                  Project enquiry received.
                </strong>

                <p
                  style={{
                    margin: '0.5rem 0 0',
                    fontSize: '0.82rem',
                  }}
                >
                  Thank you for contacting SANSA CONSULTANT.
                  Our team can review your requirements and get
                  back to you.
                </p>

                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    marginTop: '1rem',
                    border: 'none',
                    background: 'transparent',
                    color: colors.blueDark,
                    fontWeight: '800',
                    cursor: 'pointer',
                  }}
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div
                  style={styles.formGrid}
                  className="contact-form-grid"
                >

                  {/* NAME */}

                  <div style={styles.fieldWrapper}>
                    <label style={styles.label}>
                      Full Name
                    </label>

                    <input
                      name="name"
                      required
                      type="text"
                      placeholder="Your name"
                      style={styles.input(
                        focusedField === 'name'
                      )}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  {/* COMPANY */}

                  <div style={styles.fieldWrapper}>
                    <label style={styles.label}>
                      Company
                    </label>

                    <input
                      name="company"
                      type="text"
                      placeholder="Company name"
                      style={styles.input(
                        focusedField === 'company'
                      )}
                      onFocus={() =>
                        setFocusedField('company')
                      }
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  {/* EMAIL */}

                  <div style={styles.fieldWrapper}>
                    <label style={styles.label}>
                      Email Address
                    </label>

                    <input
                      name="email"
                      required
                      type="email"
                      placeholder="you@company.com"
                      style={styles.input(
                        focusedField === 'email'
                      )}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  {/* PHONE */}

                  <div style={styles.fieldWrapper}>
                    <label style={styles.label}>
                      Phone Number
                    </label>

                    <input
                      name="phone"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      style={styles.input(
                        focusedField === 'phone'
                      )}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  {/* PROJECT TYPE */}

                  <div
                    style={{
                      ...styles.fieldWrapper,
                      ...styles.fullField,
                    }}
                    className="contact-full-field"
                  >
                    <label style={styles.label}>
                      What Can We Help With?
                    </label>

                    <div
                      style={styles.projectGrid}
                      className="contact-project-grid"
                    >
                      {projectTypes.map((project) => (
                        <div
                          key={project}
                          style={styles.projectOption(
                            selectedProject === project
                          )}
                          onClick={() =>
                            setSelectedProject(project)
                          }
                        >
                          {project}
                        </div>
                      ))}
                    </div>
                    <input
                      type="hidden"
                      name="projectType"
                      value={selectedProject}
                    />
                  </div>

                  {/* MESSAGE */}

                  <div
                    style={{
                      ...styles.fieldWrapper,
                      ...styles.fullField,
                    }}
                    className="contact-full-field"
                  >
                    <label style={styles.label}>
                      Project Requirements
                    </label>

                    <textarea
                      name="message"
                      required
                      placeholder="Tell us about your project, structure type, location, drawings available, scope required, or any other important information..."
                      style={styles.textarea(
                        focusedField === 'message'
                      )}
                      onFocus={() =>
                        setFocusedField('message')
                      }
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: '1.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                    }}
                  >


                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LeErcwtAAAAAO53XtB24uQBIdv3QCvJIYxo-TjM"
                      onChange={(token) => {
                        console.log('CAPTCHA TOKEN:', token);

                        setCaptchaToken(token || '');
                        setCaptchaError('');
                      }}
                      onExpired={() => {
                        console.log('CAPTCHA expired');

                        setCaptchaToken('');
                        setCaptchaError('CAPTCHA expired. Please verify again.');
                      }}
                      onErrored={() => {
                        console.log('CAPTCHA error');

                        setCaptchaToken('');
                        setCaptchaError(
                          'CAPTCHA could not be verified. Please try again.'
                        );
                      }}
                    />



                    {captchaError && (
                      <div
                        style={{
                          color: '#dc2626',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          marginTop: '8px',
                        }}
                      >
                        {captchaError}
                      </div>
                    )}


                  </div>

                  <input
                    type="text"
                    name="website"
                    tabIndex="-1"
                    autoComplete="off"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  style={styles.submitButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      'translateY(-3px)';
                    e.currentTarget.style.boxShadow =
                      '0 20px 35px rgba(2,132,199,0.30)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform =
                      'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 15px 30px rgba(2,132,199,0.20)';
                  }}
                >
                  Send Project Enquiry
                  <span
                    style={{
                      marginLeft: '10px',
                      fontSize: '1.1rem',
                    }}
                  >
                    →
                  </span>
                </button>

                <div style={styles.responseNote}>
                  We respect project confidentiality and handle
                  submitted information responsibly.
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;